import { format } from "date-fns";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { CellState } from "./schedule-calendar-utils";
import type { DayScheduleDto } from "./schedule-shift-grid";

interface ScheduleMonthGridProps {
  monthDays: Date[];
  baseDate: Date;
  grid: Record<string, CellState>;
  editMode: boolean;
  scheduleData: DayScheduleDto[];
  toggleCell: (dateStr: string, shift: string) => void;
  setGrid: React.Dispatch<React.SetStateAction<Record<string, CellState>>>;
}

/** Monthly calendar grid showing AM/PM availability chips per day.
 *  Shared between technician and machine schedule pages.
 */
export function ScheduleMonthGrid({
  monthDays,
  baseDate,
  grid,
  editMode,
  scheduleData,
  toggleCell,
  setGrid
}: ScheduleMonthGridProps) {
  const dayShifts: Array<"AM" | "PM"> = ["AM", "PM"];

  return (
    <div className="aero:overflow-x-auto">
      <table className="aero:w-full aero:border-collapse aero:!text-sm aero:table-fixed">
        <colgroup>
          <col className="aero:w-[12%]" />
          {Array.from({ length: 7 }).map((_, i) => (
            <col key={i} className="aero:w-[12.57%]" />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="aero:border aero:border-neutral-200 aero:p-3 aero:bg-neutral-50 aero:text-left aero:font-medium aero:text-neutral-700">
              Week
            </th>
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <th
                key={d}
                className="aero:border aero:border-neutral-200 aero:p-3 aero:bg-neutral-50 aero:text-center aero:font-medium aero:text-neutral-700"
              >
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: Math.ceil(monthDays.length / 7) }).map(
            (_, weekIdx) => {
              const slice = monthDays.slice(weekIdx * 7, weekIdx * 7 + 7);
              const weekStart = slice[0];
              const weekEnd = slice[slice.length - 1];
              const startMonth = format(weekStart, "MMM");
              const endMonth = format(weekEnd, "MMM");
              const weekLabel =
                startMonth === endMonth
                  ? `${startMonth} ${format(weekStart, "d")} – ${format(weekEnd, "d")}`
                  : `${startMonth} ${format(weekStart, "d")} – ${endMonth} ${format(weekEnd, "d")}`;

              return (
                <tr key={weekIdx}>
                  <td className="aero:border aero:border-neutral-200 aero:p-3 aero:bg-neutral-50 aero:text-xs aero:align-middle">
                    <div className="aero:h-full aero:flex aero:flex-col aero:justify-center">
                      <span className="aero:font-medium aero:text-neutral-700">
                        {weekLabel}
                      </span>
                    </div>
                  </td>
                  {slice.map((day) => {
                    const dateStr = format(day, "yyyy-MM-dd");
                    const inCurrentMonth =
                      day.getMonth() === baseDate.getMonth();
                    const dayDto = scheduleData.find((x) => x.date === dateStr);
                    const isWeekend = [0, 6].includes(day.getDay());
                    const isHoliday = !!dayDto?.isHoliday;
                    const isWeekendOrHoliday = isWeekend || isHoliday;
                    const defaultAvailable = !isWeekendOrHoliday;

                    return (
                      <td
                        key={dateStr}
                        className={cn(
                          "aero:border aero:border-neutral-200 aero:align-top aero:relative aero:h-32 aero:p-2",
                          !inCurrentMonth
                            ? "aero:bg-neutral-50 aero:text-neutral-400"
                            : "aero:bg-white",
                          dayDto?.isHoliday ? "aero:bg-yellow-200" : ""
                        )}
                      >
                        {/* Date number + holiday label */}
                        <div className="aero:flex aero:flex-col aero:items-center aero:justify-start aero:h-8">
                          <span
                            className={cn(
                              "aero:text-sm aero:font-semibold",
                              !inCurrentMonth
                                ? "aero:text-neutral-400"
                                : "aero:text-foreground"
                            )}
                          >
                            {format(day, "d")}
                          </span>
                          {dayDto?.isHoliday && (
                            <span className="aero:text-[10px] aero:text-amber-700 aero:font-medium aero:leading-tight aero:text-center aero:px-1 aero:whitespace-normal aero:break-words">
                              {dayDto.holidayName || "Holiday"}
                            </span>
                          )}
                        </div>

                        {/* AM / PM shift chips */}
                        <div className="aero:mt-2 aero:space-y-1.5">
                          {dayShifts.map((shift) => {
                            const key = `${dateStr}_${shift}`;
                            const state = grid[key];
                            const isAvailable =
                              state?.available ?? defaultAvailable;
                            const isOverrideNow = state
                              ? state.available !== defaultAvailable
                              : false;

                            let bgClass: string;
                            if (isWeekendOrHoliday) {
                              bgClass = !isAvailable
                                ? isOverrideNow
                                  ? "aero:bg-red-50 aero:hover:bg-red-100"
                                  : "aero:bg-gray-200 aero:hover:bg-gray-300"
                                : "aero:bg-green-50 aero:hover:bg-green-100";
                            } else {
                              bgClass = isAvailable
                                ? "aero:bg-green-50 aero:hover:bg-green-100"
                                : "aero:bg-red-50 aero:hover:bg-red-100";
                            }

                            return (
                              <div
                                key={key}
                                className={cn(
                                  "aero:border aero:border-neutral-200 aero:rounded-md aero:px-2 aero:py-1.5 aero:text-xs aero:cursor-pointer aero:select-none aero:transition-colors aero:flex aero:flex-col aero:items-center aero:justify-center aero:h-9",
                                  bgClass,
                                  state?.dirty &&
                                    "aero:ring-2 aero:ring-blue-500 aero:ring-inset"
                                )}
                                onClick={() =>
                                  !editMode && toggleCell(dateStr, shift)
                                }
                              >
                                <span className="aero:text-[10px] aero:font-semibold aero:text-neutral-600 aero:mb-0.5">
                                  {shift}
                                </span>
                                {editMode ? (
                                  <Switch
                                    checked={isAvailable}
                                    onCheckedChange={(checked) => {
                                      setGrid((g) => {
                                        const current = g[key] || {
                                          available: isAvailable,
                                          override: false,
                                          originalAvailable: isAvailable,
                                          dirty: false
                                        };
                                        return {
                                          ...g,
                                          [key]: {
                                            ...current,
                                            available: checked,
                                            dirty:
                                              current.originalAvailable !==
                                                checked || current.dirty
                                          }
                                        };
                                      });
                                    }}
                                    className="aero:scale-75"
                                  />
                                ) : (
                                  <span
                                    className={cn(
                                      isAvailable
                                        ? "aero:text-green-700"
                                        : isWeekendOrHoliday && !isOverrideNow
                                          ? "aero:text-neutral-500"
                                          : "aero:text-red-700"
                                    )}
                                  >
                                    {isAvailable ? "✓" : "✗"}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            }
          )}
        </tbody>
      </table>
    </div>
  );
}
